define((require) => {
    "use strict";

    const {defaults, extend}         = require("underscore");
    const ImageLibraryCollectionView = require("ui/imageLibrary/ImageLibraryCollectionView");
    const ImageLibrarySourceEnum     = require("shared/data/images/ImageLibrarySourceEnum");

    /**
     * A "compact" version of the <tt>ImageLibraryView</tt> suited for smaller viewport sizes, for use primarily inside the editor.
     *
     * Supports highlighting a selected image.
     *
     * If a <tt>ProjectModel</tt> is passed, a filter dropdown will be rendered next to the search input.
     *
     * @cfg {ImageCollection} collection The <tt>ImageCollection</tt> to be rendered.
     * @cfg {ProjectModel} project The current project, if any, i.e. might be <tt>undefined</tt>.
     * @cfg {Object} itemViewOptions Any options passed to the underlying <tt>ImageThumbnailView</tt> item views.
     * @cfg {String} selectedImageId Optional image model id to represent the initial selected image, if found in the initial chunk (see #onLoadEnd).
     * @cfg {Boolean} autoSelection Optional, ensures that the <tt>CompactImageLibraryView</tt> will select the first image model in the
     *                              collection, if any, and if no <tt>selectedImageId</tt> is found or defined (see #onLoadEnd) - <tt>false</tt> by default.
     *
     * @see  ImageWidgetPropertiesView
     *
     * @class CompactImageLibraryView
     */
    return ImageLibraryCollectionView.extend("CompactImageLibraryView", {

        className: `${ImageLibraryCollectionView.prototype.className} compact`,

        options: {
            autoSelection: false
        },

        horizontalScrollBuffer: 10,

        /**
         * The options passed to the <tt>SortingPanel</tt>.
         *
         * @type {Object}
         */
        sortingPanelOptions: {
            showSources: true,
            initialSource: null
        },

        /**
         * @type {String}
         */
        _selectedImageId: null,

        constructor(options) {
            const selectedImage = options.selectedImage;

            if (selectedImage) {
                this._selectedImageId = selectedImage.get("imageId");

                if (selectedImage.get("sourceType")) {
                    this.sortingPanelOptions.initialSource = options.imageLibrarySorting.lookupSourceName(selectedImage.get("sourceType"));
                } else {
                    // If source type isn't defined on the selected image, use Creator source
                    this.sortingPanelOptions.initialSource = ImageLibrarySourceEnum.CREATOR;
                }
            } else {
                this.sortingPanelOptions.initialSource = null;
            }

            // Setup listener for the "selected" event triggered by the ImageThumbnailView - delegate the event as "image:selected"
            this.collectionViewOptions = {
                itemEvents: {
                    "selected": (imageModel) => {
                        this._updateSelectedImageState(imageModel);
                    }
                }
            };

            options.itemViewOptions = options.itemViewOptions || {};

            defaults(options.itemViewOptions, {
                draggable: false,
                isEditable: false,
                showTooltips: true,
                dropTargetSize: "small"
            });

            this.sortingPanelOptions = extend({
                project: options.project
            }, this.sortingPanelOptions, options.sortingPanelOptions);

            this.__super();
        },

        _updateSelectedImageState(imageModel, imageModelView) {
            if (!imageModel) {
                return;
            }

            const currentSelectedImage = this._collectionView.findById(this._selectedImageId);
            if (currentSelectedImage) {
                currentSelectedImage.highlight(false);
            }

            this._selectedImageId = imageModel.get("id");

            const selectedImage = imageModelView || this._collectionView.findById(this._selectedImageId);

            selectedImage.highlight(true);

            this.trigger("image:selected", imageModel);
        },

        /**
         * Invoked every time a chuck of lazy loaded image data has ended.
         *
         * If a <tt>selectedImageId</tt> has been specified, look for the
         * matching item view, if any, and highlight that.
         */
        onLoadEnd() {
            let imageModel;
            if (this._selectedImageId) {
                imageModel = this.options.collection.get(this._selectedImageId);
            }

            // If 'autoSelection' is enabled, grab the first image model in the collection...
            if (!this._selectedImageId && !imageModel && this.options.autoSelection) {
                imageModel = this.options.collection.at(0);
            }

            if (imageModel) {
                this._updateSelectedImageState(imageModel);
            }
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/imageLibrary/CompactImageLibraryView.js